'use client'

import { Button } from '@/components/ui/button'
import { SIGNATURE_INK_COLOR, SIGNATURE_PLACEHOLDER_CLASS, SIGNATURE_SURFACE_CLASS } from '@/constant/signature-surface'
import { cn } from '@/lib/utils'
import { Eraser, Undo2 } from 'lucide-react'
import { useCallback, useEffect, useImperativeHandle, useRef, useState, type RefObject } from 'react'

/** A single continuous pen movement, in CSS pixels. */
type Stroke = { x: number; y: number }[]

export interface SignaturePadHandle {
  /** Trimmed PNG data URL, or null when nothing has been drawn. */
  toDataUrl: () => string | null
  clear: () => void
  isEmpty: () => boolean
}

export interface SignaturePadProps {
  ref?: RefObject<SignaturePadHandle | null>
  onChange?: (hasInk: boolean) => void
  disabled?: boolean
  className?: string
  height?: number
  penColor?: string
}

/**
 * Canvas signature capture.
 *
 * Strokes are kept as points rather than only painted, so the canvas can be
 * re-rendered at the device pixel ratio (otherwise signatures look blurry on
 * retina displays) and so undo does not require an undo buffer of bitmaps.
 */
const SignaturePad = ({
  ref,
  onChange,
  disabled = false,
  className,
  height = 180,
  penColor = SIGNATURE_INK_COLOR,
}: SignaturePadProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const strokesRef = useRef<Stroke[]>([])
  const currentStrokeRef = useRef<Stroke | null>(null)
  const [hasInk, setHasInk] = useState(false)

  /** Repaint every stored stroke at the current canvas size and pixel ratio. */
  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const context = canvas.getContext('2d')
    if (!context) return

    const ratio = window.devicePixelRatio || 1
    const width = canvas.clientWidth
    const canvasHeight = canvas.clientHeight

    if (canvas.width !== width * ratio || canvas.height !== canvasHeight * ratio) {
      canvas.width = width * ratio
      canvas.height = canvasHeight * ratio
    }

    context.setTransform(ratio, 0, 0, ratio, 0, 0)
    context.clearRect(0, 0, width, canvasHeight)

    context.lineWidth = 2.2
    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.strokeStyle = penColor

    const allStrokes = currentStrokeRef.current ? [...strokesRef.current, currentStrokeRef.current] : strokesRef.current

    for (const stroke of allStrokes) {
      if (stroke.length === 0) continue

      context.beginPath()

      if (stroke.length === 1) {
        // A tap still leaves a mark, matching what a pen would do.
        context.arc(stroke[0].x, stroke[0].y, 1.1, 0, Math.PI * 2)
        context.fillStyle = penColor
        context.fill()
        continue
      }

      context.moveTo(stroke[0].x, stroke[0].y)
      for (let index = 1; index < stroke.length; index += 1) context.lineTo(stroke[index].x, stroke[index].y)
      context.stroke()
    }
  }, [penColor])

  useEffect(() => {
    redraw()

    const handleResize = () => redraw()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [redraw])

  /** Translate a pointer event into canvas-local coordinates. */
  const pointFrom = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }

    const rect = canvas.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  const notify = (nextHasInk: boolean) => {
    setHasInk(nextHasInk)
    onChange?.(nextHasInk)
  }

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return

    // Capture the pointer so a stroke that leaves the canvas still terminates
    // cleanly instead of leaving the pad stuck in drawing state.
    event.currentTarget.setPointerCapture(event.pointerId)
    currentStrokeRef.current = [pointFrom(event)]
    redraw()
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || !currentStrokeRef.current) return

    currentStrokeRef.current.push(pointFrom(event))
    redraw()
  }

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || !currentStrokeRef.current) return

    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId)

    strokesRef.current = [...strokesRef.current, currentStrokeRef.current]
    currentStrokeRef.current = null
    redraw()
    notify(true)
  }

  const clear = useCallback(() => {
    strokesRef.current = []
    currentStrokeRef.current = null
    redraw()
    setHasInk(false)
    onChange?.(false)
  }, [redraw, onChange])

  const undo = () => {
    strokesRef.current = strokesRef.current.slice(0, -1)
    redraw()
    notify(strokesRef.current.length > 0)
  }

  /**
   * Export the drawing as a PNG trimmed to its ink.
   *
   * Trimming matters because the pad is much wider than a signature: an untrimmed
   * export embeds a band of empty space that looks wrong in the certificate PDF.
   */
  const toDataUrl = useCallback((): string | null => {
    if (strokesRef.current.length === 0) return null

    const points = strokesRef.current.flat()
    if (points.length === 0) return null

    const padding = 8
    const minX = Math.max(0, Math.min(...points.map((point) => point.x)) - padding)
    const maxX = Math.max(...points.map((point) => point.x)) + padding
    const minY = Math.max(0, Math.min(...points.map((point) => point.y)) - padding)
    const maxY = Math.max(...points.map((point) => point.y)) + padding

    const width = Math.max(1, Math.ceil(maxX - minX))
    const canvasHeight = Math.max(1, Math.ceil(maxY - minY))

    // Export at 2x so the signature stays crisp when scaled up in a PDF.
    const scale = 2
    const exportCanvas = document.createElement('canvas')
    exportCanvas.width = width * scale
    exportCanvas.height = canvasHeight * scale

    const context = exportCanvas.getContext('2d')
    if (!context) return null

    context.scale(scale, scale)
    context.translate(-minX, -minY)
    context.lineWidth = 2.2
    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.strokeStyle = penColor
    context.fillStyle = penColor

    for (const stroke of strokesRef.current) {
      if (stroke.length === 0) continue

      if (stroke.length === 1) {
        context.beginPath()
        context.arc(stroke[0].x, stroke[0].y, 1.1, 0, Math.PI * 2)
        context.fill()
        continue
      }

      context.beginPath()
      context.moveTo(stroke[0].x, stroke[0].y)
      for (let index = 1; index < stroke.length; index += 1) context.lineTo(stroke[index].x, stroke[index].y)
      context.stroke()
    }

    return exportCanvas.toDataURL('image/png')
  }, [penColor])

  useImperativeHandle(
    ref,
    () => ({
      toDataUrl,
      clear,
      isEmpty: () => strokesRef.current.length === 0,
    }),
    [toDataUrl, clear]
  )

  return (
    <div className={cn('space-y-2', className)}>
      <div
        className={cn(
          'relative rounded-md border border-dashed',
          SIGNATURE_SURFACE_CLASS,
          disabled && 'opacity-60 pointer-events-none'
        )}
      >
        <canvas
          ref={canvasRef}
          style={{ height }}
          className='w-full touch-none rounded-md'
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerUp}
          aria-label='Signature drawing area'
          role='img'
        />

        {!hasInk && (
          <div className='pointer-events-none absolute inset-0 flex items-center justify-center'>
            <span className={cn('text-sm', SIGNATURE_PLACEHOLDER_CLASS)}>Draw your signature here</span>
          </div>
        )}

        {/* Baseline, so people sign on a line rather than floating in the box. */}
        <div className='pointer-events-none absolute inset-x-8 bottom-8 border-b border-slate-300' />
      </div>

      <div className='flex gap-2'>
        <Button type='button' variant='outline' size='sm' onClick={undo} disabled={disabled || !hasInk}>
          <Undo2 className='h-3.5 w-3.5' />
          Undo
        </Button>
        <Button type='button' variant='outline' size='sm' onClick={clear} disabled={disabled || !hasInk}>
          <Eraser className='h-3.5 w-3.5' />
          Clear
        </Button>
      </div>
    </div>
  )
}

export default SignaturePad
